import React from 'react';
import { cn } from 'src/utils/cn';
import trueLogo from 'src/assets/true-logo.svg';

/**
 * Size variants for the AuthLogo component
 */
export type AuthLogoSize = 'sm' | 'md' | 'lg';

/**
 * Props for the AuthLogo component
 */
export interface AuthLogoProps {
  /**
   * Size variant for the logo
   * - sm: h-8 (32px) - Compact size for smaller contexts
   * - md: h-10 (40px) - Default size for login pages
   * - lg: h-12 (48px) - Larger size for prominent display
   */
  size?: AuthLogoSize;

  /**
   * Additional CSS classes to apply to the logo
   */
  className?: string;

  /**
   * Whether to include bottom margin (mb-4)
   * Useful for components that need spacing below the logo
   */
  withBottomMargin?: boolean;
}

/**
 * Size class mapping for different logo variants
 */
const SIZE_CLASSES: Record<AuthLogoSize, string> = {
  sm: 'h-8', // 32px - Compact
  md: 'h-10', // 40px - Default
  lg: 'h-12', // 48px - Large
} as const;

/**
 * AuthLogo Component
 *
 * A reusable logo component for authentication screens that consolidates
 * duplicate logo rendering logic across the authentication system.
 *
 * Features:
 * - Consistent True Intelligence branding
 * - Configurable size variants (sm, md, lg)
 * - Optional additional CSS classes
 * - Automatic centering with mx-auto
 * - Proper accessibility with alt text
 * - Optional bottom margin for spacing
 *
 * @example
 * ```tsx
 * // Default medium size
 * <AuthLogo />
 *
 * // Large size with bottom margin
 * <AuthLogo size="lg" withBottomMargin />
 *
 * // Small size with custom classes
 * <AuthLogo size="sm" className="opacity-75" />
 * ```
 */
export const AuthLogo: React.FC<AuthLogoProps> = ({
  size = 'md',
  className,
  withBottomMargin = false,
}) => {
  const logoClasses = cn(
    SIZE_CLASSES[size],
    'w-auto mx-auto',
    withBottomMargin && 'mb-4',
    className,
  );

  return <img src={trueLogo} alt="True Intelligence" className={logoClasses} />;
};

export default AuthLogo;
